import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Alert, Spin, Card, Row, Col, Switch } from 'antd';
import { FileTextOutlined, PercentageOutlined } from '@ant-design/icons';
import { clinicSettingsService } from '../../services/clinicSettingsService';
import { itbisTypeService, type ItbisType } from '../../services/itbisTypeService';
import type { ClinicSettings } from '../../types/clinic';

interface FiscalFormValues {
    rnc: string | null;
    dgii_authorization_number: string | null;
    economic_activity_code: string | null;
    fiscal_establishment: string | null;
    fiscal_point_of_sale: string | null;
}

const RNC_PATTERN = /^\d{9}$|^\d{11}$/;

/**
 * Settings → Fiscal tab (task 5.4). Renders a form with the 5
 * DGII fiscal fields defined in spec clinic-fiscal-settings R1:
 * rnc, dgii_authorization_number, economic_activity_code,
 * fiscal_establishment, fiscal_point_of_sale. Persists via
 * clinicSettingsService.updateSettings() (which already supports
 * these fields per backend PR 2 @ 075ff1b).
 */
const FiscalTab: React.FC = () => {
    const [form] = Form.useForm<FiscalFormValues>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [settings, setSettings] = useState<ClinicSettings | null>(null);
    const [itbisTypes, setItbisTypes] = useState<ItbisType[]>([]);
    const [itbisDrafts, setItbisDrafts] = useState<Record<number, { name: string; ratePercent: number; is_active: boolean }>>({});
    const [savingItbisId, setSavingItbisId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            const [settingsResult, typesResult] = await Promise.allSettled([
                clinicSettingsService.getSettings(),
                itbisTypeService.getAll(),
            ]);
            if (cancelled) return;

            const errors: string[] = [];

            if (settingsResult.status === 'fulfilled') {
                const data = settingsResult.value;
                setSettings(data);
                form.setFieldsValue({
                    rnc: data.rnc ?? '',
                    dgii_authorization_number: data.dgii_authorization_number ?? '',
                    economic_activity_code: data.economic_activity_code ?? '',
                    fiscal_establishment: data.fiscal_establishment ?? '',
                    fiscal_point_of_sale: data.fiscal_point_of_sale ?? '',
                });
            } else {
                errors.push(
                    settingsResult.reason instanceof Error
                        ? settingsResult.reason.message
                        : 'Error al cargar configuración fiscal'
                );
            }

            if (typesResult.status === 'fulfilled') {
                const types = typesResult.value;
                setItbisTypes(types);
                setItbisDrafts(Object.fromEntries(types.map((t) => [t.id, {
                    name: t.name,
                    ratePercent: Number(t.rate) * 100,
                    is_active: t.is_active,
                }])));
            } else {
                errors.push(
                    typesResult.reason instanceof Error
                        ? typesResult.reason.message
                        : 'Error al cargar tipos de ITBIS'
                );
            }

            setError(errors.length ? errors.join(' — ') : null);
            setLoading(false);
        }
        load();
        return () => { cancelled = true; };
    }, [form]);

    const saveItbisType = async (type: ItbisType) => {
        const draft = itbisDrafts[type.id];
        if (!draft) return;
        setSavingItbisId(type.id);
        setError(null);
        setSuccess(null);
        try {
            const updated = await itbisTypeService.update(type.id, {
                name: draft.name,
                rate: draft.ratePercent / 100,
                is_active: draft.is_active,
            });
            setItbisTypes((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSuccess('Tipo de ITBIS guardado');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al guardar tipo de ITBIS');
        } finally {
            setSavingItbisId(null);
        }
    };

    const onFinish = async (values: FiscalFormValues) => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const updated = await clinicSettingsService.updateSettings({
                rnc: values.rnc || null,
                dgii_authorization_number: values.dgii_authorization_number || null,
                economic_activity_code: values.economic_activity_code || null,
                fiscal_establishment: values.fiscal_establishment || null,
                fiscal_point_of_sale: values.fiscal_point_of_sale || null,
            });
            setSettings(updated);
            setSuccess('Configuración fiscal guardada');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spin size="large" />
            </div>
        );
    }

    const hasRnc = !!settings?.rnc;

    return (
        <>
        <Card
            title={
                <span>
                    <FileTextOutlined /> Configuración Fiscal (DGII)
                </span>
            }
        >
            {settings && !hasRnc && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-3"
                    message="Sin RNC configurado"
                    description="El flujo fiscal está deshabilitado. Configure el RNC del consultorio para habilitar comprobantes fiscales (NCF)."
                />
            )}

            {error && <Alert type="error" showIcon className="mb-3" message={error} />}
            {success && <Alert type="success" showIcon className="mb-3" message={success} />}

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="rnc"
                            label="RNC"
                            rules={[
                                {
                                    pattern: RNC_PATTERN,
                                    message: 'RNC debe ser 9 dígitos (empresa) o 11 dígitos (persona)',
                                },
                            ]}
                        >
                            <Input placeholder="130123456 o 00112345678" maxLength={11} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="dgii_authorization_number"
                            label="Autorización DGII"
                        >
                            <Input placeholder="DGII-AUTH-2026-001" maxLength={50} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="economic_activity_code"
                            label="Actividad Económica"
                        >
                            <Input placeholder="85110" maxLength={20} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            name="fiscal_establishment"
                            label="Establecimiento Fiscal"
                        >
                            <Input placeholder="001" maxLength={50} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            name="fiscal_point_of_sale"
                            label="Punto de Emisión"
                        >
                            <Input placeholder="001" maxLength={20} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={saving}>
                        Guardar Configuración Fiscal
                    </Button>
                </Form.Item>
            </Form>
        </Card>

        <Card
            className="mt-3"
            title={
                <span>
                    <PercentageOutlined /> Tipos de ITBIS
                </span>
            }
        >
            {itbisTypes.map((type) => {
                const draft = itbisDrafts[type.id] ?? {
                    name: type.name,
                    ratePercent: Number(type.rate) * 100,
                    is_active: type.is_active,
                };
                return (
                    <Row key={type.id} gutter={16} align="middle" className="mb-2">
                        <Col span={8}>
                            <label className="d-block" htmlFor={`itbis-name-${type.code}`}>Nombre</label>
                            <Input
                                id={`itbis-name-${type.code}`}
                                value={draft.name}
                                onChange={(e) => setItbisDrafts((prev) => ({
                                    ...prev,
                                    [type.id]: { ...draft, name: e.target.value },
                                }))}
                            />
                        </Col>
                        <Col span={6}>
                            <label className="d-block" htmlFor={`itbis-rate-${type.code}`}>
                                Tasa {type.code} (%)
                            </label>
                            <Input
                                id={`itbis-rate-${type.code}`}
                                type="number"
                                min={0}
                                max={100}
                                step={0.01}
                                value={draft.ratePercent}
                                onChange={(e) => setItbisDrafts((prev) => ({
                                    ...prev,
                                    [type.id]: { ...draft, ratePercent: Number(e.target.value ?? 0) },
                                }))}
                            />
                        </Col>
                        <Col span={4}>
                            <label className="d-block">Activo</label>
                            <Switch
                                checked={draft.is_active}
                                onChange={(checked) => setItbisDrafts((prev) => ({
                                    ...prev,
                                    [type.id]: { ...draft, is_active: checked },
                                }))}
                            />
                        </Col>
                        <Col span={6}>
                            <Button
                                type="primary"
                                aria-label={`Guardar tipo ${type.code}`}
                                loading={savingItbisId === type.id}
                                onClick={() => saveItbisType(type)}
                            >
                                Guardar tipo {type.code}
                            </Button>
                        </Col>
                    </Row>
                );
            })}
        </Card>
        </>
    );
};

export default FiscalTab;
